<template>
  <a-modal
    title="原始数据 (仅返回50条数据)"
    width="1300px"
    v-model:open="open"
    :maskClosable="false"
    centered
    @cancel="handleCancel"
  >
    <div>
      <a-card
        size="small"
        :bordered="false"
        style="margin-bottom: 12px"
      >
        <a-space
          direction="vertical"
          :size="8"
          style="width: 100%"
        >
          <div style="display: flex; justify-content: space-between; align-items: center">
            <div style="font-weight: 600">查询条件</div>
            <div>
              <a-button
                size="small"
                @click="resetConditions"
              >
                重置
              </a-button>
              <a-button
                type="primary"
                size="small"
                style="margin-left: 8px"
                @click="applySearch"
              >
                查询
              </a-button>
            </div>
          </div>
          <div class="conditions-scroll">
            <ConditionGroup
              :group="conditionsRoot"
              :columns="column"
              :is-root="true"
            />
          </div>
          <div class="time-range-row">
            <span class="field-label">时间范围：</span>
            <a-range-picker
              v-model:value="timeRange"
              :show-time="{ format: 'HH:mm:ss' }"
              format="YYYY-MM-DD HH:mm:ss"
              value-format="YYYY-MM-DD HH:mm:ss"
              :placeholder="['开始时间', '结束时间']"
              style="width: 440px"
            />
          </div>
        </a-space>
      </a-card>
      <a-spin :spinning="loading">
        <s-table
          v-if="datasource?.length"
          :columns="column"
          :dataSource="datasource"
          rowKey="_id"
          :pagination="false"
          :scroll="{ y: height, x: width }"
          @change="handleTableChange"
        >
          <template #headerCell="{ title, column }">
            <a-tag
              color="blue"
              v-if="column.isMatch"
            >
              {{ title }}
            </a-tag>
            <span v-if="!column.isMatch">
              {{ title }}
            </span>
          </template>
        </s-table>
      </a-spin>
    </div>
    <template #footer>
      <a-button
        key="back"
        @click="handleCancel"
      >
        关闭
      </a-button>
      <a-button
        key="ok"
        type="primary"
        @click="handleCancel"
      >
        确定
      </a-button>
    </template>
  </a-modal>
</template>

<script lang="ts">
import { defineComponent, onUnmounted, ref, onMounted, computed } from 'vue';
import { debounce } from 'lodash';
import ConditionGroup from './syncConditionGroup.vue';
import { getApiData } from 'liyu-pc-base/api/api-model';
import dayjs from 'dayjs';

export default defineComponent({
  name: 'ExportRawData',
  components: { ConditionGroup },
  props: {
    title: {
      type: String,
      required: false,
      default: () => '',
    },
    record: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const height = ref(window.innerHeight - 550);
    const onresize: any = debounce(() => {
      height.value = window.innerHeight - 550;
    }, 200);
    const loading = ref(false);

    const datasource = ref<any[]>([]);
    const open = ref(true);
    const titleMap = ref({});

    const column = computed(() => {
      const res = [];
      if (datasource.value?.length) {
        for (const key of Object.keys(datasource.value[0])) {
          if (key != '_id') {
            const title = titleMap.value[key] || key;
            res.push({
              title: title,
              dataIndex: key,
              key: key,
              isMatch: !!titleMap.value[key],
            });
          }
        }
      }
      return res;
    });

    const get_column_config = async () => {
      const res: any = await getApiData(
        {
          export_task_id: (props as any).record?.id,
        },
        `${props.record.model_app}/query_sync_model_field_list`,
      );
      if (res?.data?.length) {
        for (const item of res.data) {
          titleMap.value[item.field_name] = item.name;
          titleMap.value[item.name] = item.name;
        }
      }
    };
    const current = ref<number>(1);
    const pageSize = ref<number>(10);
    const total = ref<number>(0);
    const width = computed(() => {
      let w = column.value.length * 200;
      return w < 1000 ? 1000 : w;
    });
    const pageOptions = ref<string[]>([
      '10',
      '20',
      '50',
      '100',
      '500',
      '1000',
      '2000',
      '5000',
      '10000',
    ]);

    // 条件构建器根
    const conditionsRoot = ref<any>({ type: 'and', conditions: [] });

    // 时间范围（[start, end] 字符串，格式 YYYY-MM-DD HH:mm:ss；用于后端 start_time/end_time）
    const timeRange = ref<any>(null);

    // 排序状态 - 支持多字段排序
    const sortFields = ref<Array<{ field: string; order: string }>>([]);

    const handleCancel = () => {
      emit('cancel');
    };

    const get_datasource = async () => {
      loading.value = true;
      try {
        const params: any = {
          export_task_id: (props as any).record?.id,
          current: current.value,
          pageSize: pageSize.value,
          conditions: conditionsRoot.value,
          sort_fields: sortFields.value.length > 0 ? sortFields.value : undefined,
        };
        // 时间范围：前端传则带上（供后端作为 start_time/end_time 渲染 SQL），不传则回退默认区间
        if (
          timeRange.value &&
          timeRange.value.length === 2 &&
          timeRange.value[0] &&
          timeRange.value[1]
        ) {
          params.time_range = timeRange.value;
        }
        const res: any = await getApiData(params, `${props.record.model_app}/query_raw_data`);

        const tmpData = res?.data || [];
        let index = 0;
        for (const item of tmpData) {
          item._id = index;
          index++;
        }
        datasource.value = tmpData;
        current.value = res?.current ?? current.value;
        pageSize.value = res?.pageSize ?? pageSize.value;
        total.value = res?.total ?? 0;
      } finally {
        loading.value = false;
      }
    };

    const handlePageChange = (page: number, size?: number) => {
      current.value = page;
      if (size && size !== pageSize.value) pageSize.value = size;
      get_datasource();
    };

    const handleTableChange = (pagination: any, filters: any, sorter: any, { action }: any) => {
      if (action === 'sort') {
        if (sorter?.field) {
          const field = sorter.field;
          const order = sorter.order === 'ascend' ? 'asc' : 'desc';
          sortFields.value = [{ field: field, order: order }];
        } else {
          // 清除所有排序
          sortFields.value = [];
        }

        // 重置到第一页并重新查询
        current.value = 1;
        get_datasource();
      }
    };

    const resetConditions = () => {
      conditionsRoot.value = { type: 'and', conditions: [] };
      timeRange.value = null;
    };
    const applySearch = () => {
      current.value = 1;
      get_datasource();
    };

    onMounted(() => {
      window.addEventListener('resize', onresize);
      onresize();
      get_column_config();
      let start_time = '';
      if (props.record?.data_start_date) {
        start_time = props.record?.data_start_date + ' 00:00:00';
      } else {
        start_time = dayjs().subtract(1, 'day').format('YYYY-MM-DD HH:mm:ss');
      }
      timeRange.value = [start_time, dayjs().format('YYYY-MM-DD HH:mm:ss')];
    });
    onUnmounted(() => {
      window.removeEventListener('resize', onresize);
    });
    return {
      height,
      handleCancel,
      open,
      column,
      width,
      datasource,
      current,
      pageSize,
      total,
      handlePageChange,
      pageOptions,
      // 条件
      conditionsRoot,
      resetConditions,
      applySearch,
      loading,
      handleTableChange,
      // 时间范围
      timeRange,
    };
  },
});
</script>
<style lang="less" scoped>
.qb-group {
  border-left: 2px solid #f0f0f0;
  padding-left: 8px;
}
.qb-group-head {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}
.qb-group-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.qb-rule {
  display: flex;
  align-items: center;
  gap: 8px;
}
.qb-subgroup {
  padding: 6px 0 6px 8px;
  border-left: 2px dashed #f0f0f0;
}
.conditions-scroll {
  max-height: 200px;
  overflow-y: auto;
  padding-right: 8px;
}
.time-range-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.field-label {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
}
</style>
